import { memo, useEffect, type ReactNode } from "react";
import { message, Spin, Tooltip, Typography } from "antd";
import { LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { useWidgetStatus, useWidgetError } from "../Contexts/dashboardHooks";

const { Text } = Typography;

interface WidgetShellProps {
  id: string;
  children: ReactNode;
}

const FRIENDLY_MESSAGES: Record<string, string> = {
  timeout: "Request timed out. Try reloading the dashboard.",
  "network error": "Network error. Check your connection and try again.",
  "500": "Something went wrong on the server. Please try again later.",
  "503": "Service temporarily unavailable. Please try again later.",
  "404": "Data not found. The widget may be misconfigured.",
  "403": "You don't have permission to view this data.",
  "401": "Your session has expired. Please log in again.",
};

// Backend per-widget guard messages are written to be actionable (e.g. "No baseline
// found for the selected players, metric 'top_speed'") — show them verbatim when they
// read as one clean sentence; anything technical falls back to the generic line.
function looksHumanReadable(raw: string): boolean {
  return (
    raw.length <= 160 &&
    !raw.includes("\n") &&
    !/traceback|exception|<|http|\{/i.test(raw)
  );
}

function friendlyMessage(raw: string | null): string {
  if (!raw) return "Failed to load widget data.";
  const lower = raw.toLowerCase();
  for (const [key, msg] of Object.entries(FRIENDLY_MESSAGES)) {
    if (lower.includes(key)) return msg;
  }
  if (looksHumanReadable(raw)) return raw;
  return "Failed to load widget data. Please try again later.";
}

/**
 * Wraps a widget's content, gating on its data status.
 * Shows a spinner while loading, an error message on failure,
 * or renders children when ready. The refresh state (a slicer edit awaiting fresh data)
 * renders in the corner strip (ConnectedSlicerBadges), not here.
 */
export const WidgetShell = memo(function WidgetShell({ id, children }: WidgetShellProps) {
  const status = useWidgetStatus(id);
  const rawError = useWidgetError(id);

  useEffect(() => {
    if (status === "error" && rawError) {
      message.error(friendlyMessage(rawError));
    }
  }, [status, rawError]);

  if (status === "loading") {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </div>
    );
  }

  if (status === "error") {
    const friendly = friendlyMessage(rawError);
    return (
      <Tooltip title={rawError} placement="bottom">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 8,
            padding: 16,
            color: "#999",
          }}
        >
          <WarningOutlined style={{ fontSize: 24, color: "#faad14" }} />
          <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
            {friendly}
          </Text>
        </div>
      </Tooltip>
    );
  }

  return <>{children}</>;
});
